// Product detail page: gallery thumb swap, real Shopify variant picker,
// quantity stepper feed, and the real add-to-cart call. Everything that
// used to be "find this product by ?handle= in a giant JS array" in the
// prototype is gone — the server already rendered the right product's
// title/price/gallery/description directly (see main-product.liquid).
// This file only wires up interactivity on top of that real markup.
(function () {
  const root = document.querySelector('[data-pd-root]');
  if (!root || typeof window.__productJSON === 'undefined') return;

  const product = window.__productJSON;
  const hasGsap = typeof window.gsap !== 'undefined';
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- Gallery ---------------- */
  const mainImg = document.querySelector('[data-pd-main-img]');
  const thumbsWrap = document.querySelector('[data-gallery-thumbs]');
  if (thumbsWrap) {
    const thumbButtons = Array.from(thumbsWrap.querySelectorAll('[data-gallery-thumb]'));
    thumbButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const nextSrc = btn.dataset.src;
        if (nextSrc === mainImg.getAttribute('src')) return;
        thumbButtons.forEach((b) => b.classList.remove('ring-2', 'ring-[var(--color-gold)]'));
        btn.classList.add('ring-2', 'ring-[var(--color-gold)]');
        // Match the main image's crop behaviour to whichever photo is now
        // showing — a wide flat-lay shot needs object-contain, a normal
        // tightly-cropped one looks better filling the frame with
        // object-cover. See main-product.liquid for how data-fit is set.
        const swap = () => {
          mainImg.src = nextSrc;
          const fit = btn.dataset.fit; // e.g. "object-contain" or "object-cover scale-125"
          if (fit) {
            mainImg.classList.remove('object-cover', 'object-contain', 'scale-125');
            fit.split(' ').forEach((cls) => mainImg.classList.add(cls));
          }
        };
        if (hasGsap && !prefersReducedMotion) {
          gsap.to(mainImg, { opacity: 0, duration: 0.15, onComplete: () => { swap(); gsap.to(mainImg, { opacity: 1, duration: 0.25 }); } });
        } else {
          swap();
        }
      });
    });
  }

  /* ---------------- Learn more / less ---------------- */
  const summaryEl = document.querySelector('[data-pd-summary]');
  const descriptionEl = document.querySelector('[data-pd-description]');
  const learnMoreBtn = document.querySelector('[data-pd-learn-more]');
  const learnMoreLabel = document.querySelector('[data-pd-learn-more-label]');
  const learnMoreIcon = document.querySelector('[data-pd-learn-more-icon]');
  if (learnMoreBtn) {
    let expanded = false;
    learnMoreBtn.addEventListener('click', () => {
      expanded = !expanded;
      summaryEl.classList.toggle('hidden', expanded);
      descriptionEl.classList.toggle('hidden', !expanded);
      learnMoreLabel.textContent = expanded ? 'Show less' : 'Learn more';
      learnMoreIcon.style.transform = expanded ? 'rotate(90deg)' : 'none';
    });
  }

  /* ---------------- Variant picker ----------------
     Standard Shopify pattern: track one selected value per real option,
     look up the matching variant from product.variants (already the full,
     real list — no fetch needed), and reflect it in price/SKU/weight/
     availability/Add To Cart. Ring-size-gated products (see
     main-product.liquid's `requires_selection`) start with nothing
     selected and Add To Cart disabled until a full, valid combination is
     chosen — "no size is ever pre-selected" carried over unchanged from
     the approved design's reasoning about the cost of a wrong ring size. */
  const variantWrap = document.querySelector('[data-pd-variant-wrap]');
  const addCartBtn = document.querySelector('[data-add-cart]');
  const addCartLabel = addCartBtn?.querySelector('[data-label]');
  const qtyControls = document.querySelectorAll('[data-qty-step], [data-qty-value]');
  const priceEl = document.querySelector('[data-pd-price]');
  const skuRow = document.querySelector('[data-pd-spec-sku] p:last-child');
  const weightRow = document.querySelector('[data-pd-spec-weight] p:last-child');

  function formatMoney(amount) {
    return window.formatMoney ? window.formatMoney(amount) : `R${Math.round(amount)}`;
  }

  function setAddCartState(variant, gateOpenLabel) {
    if (!addCartBtn) return;
    const locked = !variant || !variant.available;
    addCartBtn.disabled = locked;
    addCartBtn.classList.toggle('opacity-40', locked);
    addCartBtn.classList.toggle('cursor-not-allowed', locked);
    qtyControls.forEach((el) => { el.disabled = locked; });
    if (addCartLabel) {
      if (!variant) addCartLabel.textContent = gateOpenLabel;
      else if (!variant.available) addCartLabel.textContent = 'Sold Out';
      else addCartLabel.textContent = 'Add To Cart';
    }
    if (variant) {
      addCartBtn.dataset.variantId = variant.id;
      if (priceEl) {
        priceEl.innerHTML = (variant.compare_at_price && variant.compare_at_price > variant.price)
          ? `<span class="line-through text-inksoft/50 text-xl mr-2">${formatMoney(variant.compare_at_price / 100)}</span>${formatMoney(variant.price / 100)}`
          : formatMoney(variant.price / 100);
      }
      if (skuRow && variant.sku) skuRow.textContent = variant.sku;
      if (weightRow && variant.grams) weightRow.textContent = `${(variant.grams)} g`;
      // Keep the Payflex installment breakdown in sync with whichever
      // variant is now selected — same real API Payflex's own docs use.
      if (typeof window.PayflexWidget !== 'undefined' && window.PayflexWidget.update) {
        window.PayflexWidget.update(variant.price / 100);
      }
    } else {
      delete addCartBtn.dataset.variantId;
    }
  }

  if (variantWrap) {
    const requiresSelection = variantWrap.dataset.requiresSelection === 'true';
    const groups = Array.from(variantWrap.querySelectorAll('[data-pd-option-group]'));
    const selections = groups.map((group) => {
      const active = group.querySelector('.is-active');
      return active ? active.dataset.value : null;
    });

    function findVariant() {
      if (selections.some((v) => v === null)) return null;
      return product.variants.find((v) => {
        const opts = [v.option1, v.option2, v.option3];
        return selections.every((sel, i) => opts[i] === sel);
      }) || null;
    }

    const gateLabel = addCartLabel ? addCartLabel.textContent : 'Select An Option';

    groups.forEach((group, i) => {
      group.querySelectorAll('[data-pd-variant-option]').forEach((btn) => {
        btn.addEventListener('click', () => {
          group.querySelectorAll('[data-pd-variant-option]').forEach((b) => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          selections[i] = btn.dataset.value;
          setAddCartState(findVariant(), gateLabel);
        });
      });
    });

    if (requiresSelection) {
      setAddCartState(null, gateLabel);
    }
  }

  /* ---------------- Add To Cart (real cart write) ----------------
     Deliberately separate from main.js's delegated Quick Add handler
     (which always adds a fixed default variant) because this button must
     add whichever variant the shopper just selected above. */
  addCartBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    if (addCartBtn.disabled || addCartBtn.dataset.state === 'loading') return;
    const variantId = addCartBtn.dataset.variantId;
    if (!variantId) return;
    const qtyInput = document.querySelector('[data-qty-value]');
    const quantity = Math.max(1, parseInt(qtyInput?.value, 10) || 1);
    const originalLabel = addCartLabel ? addCartLabel.textContent : 'Add To Cart';

    addCartBtn.dataset.state = 'loading';
    addCartBtn.style.pointerEvents = 'none';
    if (addCartLabel) addCartLabel.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] }),
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error((data && data.description) || 'Could not add to cart');
        if (addCartLabel) addCartLabel.textContent = 'Added ✓';
        addCartBtn.dataset.state = 'done';
        return window.refreshCartCount ? window.refreshCartCount() : null;
      })
      .catch(() => {
        if (addCartLabel) addCartLabel.textContent = 'Could Not Add — Try Again';
        addCartBtn.dataset.state = 'done';
      })
      .finally(() => {
        setTimeout(() => {
          addCartBtn.dataset.state = '';
          addCartBtn.style.pointerEvents = '';
          if (addCartLabel) addCartLabel.textContent = originalLabel;
        }, 1800);
      });
  });
})();
